import type { ComponentType, ReactNode } from 'react';

import { BasePanel } from '../base/BasePanel';
import type { BaseAppearance, BasePanelClassNames, BasePanelProps } from '../base/types';
import { cn } from '../base/utils';

export interface BentoPanelProps
  extends Omit<BasePanelProps, 'children' | 'className' | 'classNames' | 'header' | 'title'> {
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  title?: ReactNode;
  titleIcon?: ComponentType<{ className?: string; color?: string; stroke?: string }>;
  action?: ReactNode;
  classNames?: Omit<BasePanelClassNames, 'header' | 'body'> & {
    header?: string;
    body?: string;
  };
}

const defaultAppearance: BaseAppearance = {
  preset: 'soft',
  radius: 'lg',
  border: 'subtle',
  background: 'muted',
};

export function BentoPanel({
  children,
  className,
  contentClassName,
  title,
  titleIcon: TitleIcon,
  action,
  appearance,
  classNames,
  ...props
}: BentoPanelProps) {
  return (
    <BasePanel
      as="section"
      appearance={{
        ...defaultAppearance,
        ...appearance,
        slots: {
          ...appearance?.slots,
          // BasePanel's lg preset enlarges panels; Bento uses the theme baseline.
          panelRoot: cn(appearance?.radius === undefined && 'rounded-lg', appearance?.slots?.panelRoot),
        },
      }}
      classNames={{
        ...classNames,
        root: cn('min-h-0', classNames?.root, className),
        header: cn(
          'flex min-w-0 items-center gap-3 border-b border-border px-4 py-3',
          classNames?.header,
        ),
        body: cn('min-h-0 p-0', classNames?.body, contentClassName),
      }}
      header={
        title ? (
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              {TitleIcon ? (
                <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-primary/30 bg-primary/10 text-primary">
                  <TitleIcon className="size-4 text-primary" color="currentColor" stroke="currentColor" />
                </span>
              ) : null}
              <h2 className="min-w-0 truncate text-[15px] font-semibold leading-5 text-foreground">{title}</h2>
            </div>
            {action ? <div className="shrink-0">{action}</div> : null}
          </div>
        ) : undefined
      }
      {...props}
    >
      {children}
    </BasePanel>
  );
}
